import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthStore } from '../../store/authStore';
import {
  Home,
  Key,
  FileText,
  User,
  CreditCard,
  Send,
  Phone,
  Book,
  LogOut,
  X,
} from 'lucide-react';
import '../../i18n';
import BRAND from '../../brand';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const locale = useAuthStore((state) => state.locale);
  const { t } = useTranslation();

  // Auto-close sidebar on mobile when route changes
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [location.pathname, setSidebarOpen, sidebarOpen]);

  const menuItems = [
    { icon: Home, label: t('Dashboard'), path: '/dashboard' },
    { icon: Key, label: t('API Keys'), path: '/dashboard/api-keys' },
    { icon: Phone, label: t('Link Phone Numbers'), path: '/dashboard/link-phones' },
    { icon: FileText, label: t('Logs'), path: '/dashboard/logs' },
    { icon: CreditCard, label: t('Billing'), path: '/dashboard/billing' },
    { icon: User, label: t('Profile'), path: '/dashboard/profile' },
    { icon: Send, label: t('Try Sending OTP'), path: '/dashboard/send-message' },
    { icon: Book, label: t('API Docs'), external: true, href: '/otp/api-docs' },
  ];

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  // Handler for "coming soon"
  const handleComingSoon = (e) => {
    e.preventDefault();
    toast.info(locale === 'ar' ? 'قريبا' : 'Coming soon');
  };

  return (
    <div
      className={`sidebar-wrapper ${sidebarOpen ? 'open' : 'closed'} ${
        locale === 'ar' ? 'rtl' : 'ltr'
      }`}
    >
      {/* Close button for mobile */}
      <button
        className="sidebar-close-mobile"
        onClick={() => setSidebarOpen(false)}
      >
        <X size={24} />
      </button>

      {/* Logo */}
      <div className="sidebar-logo">
        <Link to="/dashboard" className="logo-link">
          <span className="logo-brand">{BRAND[i18n.language] || BRAND.en}</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="sidebar-navigation">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = !item.external && location.pathname === item.path;

          if (item.external) {
            return (
              <a
                key={item.label}
                href={item.href}
                className="nav-link external"
                target="_blank"
                rel="noopener noreferrer"
                title={item.label}
              >
                <Icon size={20} />
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </a>
            );
          }

          // Lock the Link Phone Numbers item
          if (item.path === '/dashboard/link-phones') {
            return (
              <a
                key={item.path}
                href="#"
                className={`nav-link${isActive ? ' active' : ''}`}
                title={!sidebarOpen ? item.label : ''}
                onClick={handleComingSoon}
                style={{ cursor: 'not-allowed', opacity: 0.6 }}
              >
                <Icon size={20} />
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </a>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${isActive ? 'active' : ''}`}
              title={!sidebarOpen ? item.label : ''}
            >
              <Icon size={20} />
              {sidebarOpen && <span className="nav-label">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="sidebar-user-section">
        <div className="user-card">
          <div className="user-avatar">
            {user?.first_name?.charAt(0).toUpperCase()}
          </div>
          {sidebarOpen && (
            <div className="user-info">
              <p className="user-name">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="user-email">{user?.email}</p>
            </div>
          )}
        </div>
        <button
          className="logout-btn"
          onClick={handleLogout}
          title="Sign Out"
        >
          <LogOut size={18} />
          {sidebarOpen && <span>Sign Out</span>}
        </button>
      </div>
    </div>
  );
}
